import { useToast } from '@chakra-ui/react';
import { ReactNode, createContext, useContext, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation } from 'urql';
import { v4 as uuid } from 'uuid';
import { Point } from '../../domain/geometry/geometric-types';
import {
  CreateCommentInput,
  CreateCommentMutationDocument,
  DeleteCommentDocument,
} from '../../gql/graphql';
import { useUserTenant } from '../../services/auth-info';
import { EventName, useMixpanel } from '../../services/mixpanel';
import { hasDbIds } from '../../services/viewer-services';
import { useBreadcrumb } from '../common/BreadcrumbProvider';
import { useCommentsPopoverAttributes } from '../common/CommentsPopoverProvider';
import { DbIdsPerModel, useViewer } from '../common/ForgeViewer';
import { getCameraPosition } from '../common/forgeViewerInteractions/savedViews/SavedViewsMenu';

const CommentsContext = createContext<any | null>(null);

export function useComments() {
  return useContext(CommentsContext);
}

interface Comment {
  id: string;
  createdAt: Date;
  content: string;
  user: {
    username: string;
  };
  replies: Comment[];
  replyingTo?: string;
  projectId: string;
  takeoffId?: string;
  selectedDbIds?: DbIdsPerModel;
  parentId?: string;
  point?: Point;
}

interface CommentsProviderProps {
  children: ReactNode;
}

function CommentsProvider({ children }: CommentsProviderProps) {
  const [showDeleteDialog, setDeleteDialogVisibility] =
    useState<boolean>(false);
  const [currentCommentIdx, setCurrentCommentIdx] = useState<{
    id: number | null;
    parentId: number | null;
  }>({
    id: null,
    parentId: null,
  });

  const {
    viewer,

    visibilityManager: { hiddenElements, isolatedElements, hiddenModelUrns },
  } = useViewer();
  const { selectedCategories, selectedLayers, selectedOrderStatuses } =
    useCommentsPopoverAttributes();

  const [, createCommentMutation] = useMutation(CreateCommentMutationDocument);
  const [, deleteCommentMutation] = useMutation(DeleteCommentDocument);

  const user = useUserTenant();

  const { projectId, orderId } = useParams();
  const { projectName } = useBreadcrumb();

  const toast = useToast();
  const navigate = useNavigate();

  const { trackEvent } = useMixpanel();

  async function saveChanges(newComment: Comment) {
    const dbIdsGroupedByModel: DbIdsPerModel = newComment.selectedDbIds
      ? newComment.selectedDbIds
      : {};

    const createCommentInput: CreateCommentInput = {
      comment: {
        commentToParentId: undefined,
        commentToReplies: undefined,
        content: newComment.content,
        orderId: orderId,
        parentId: newComment.parentId,
        projectId: projectId,
        readBy: [user.username],
        replies: undefined,
        username: user.username,
        elementIdentifiers: Object.keys(dbIdsGroupedByModel).map((modelId) => {
          return { modelUrn: modelId, dbIds: dbIdsGroupedByModel[modelId] };
        }),
        anchorPoint3D: newComment.point
          ? {
              x: newComment.point?.[0],
              y: newComment.point?.[1],
              z: newComment.point?.[2],
            }
          : undefined,
        savedView: {
          create: {
            name: uuid(),
            projectId,
            section: null,
            categories: Array.from(selectedCategories),
            layers: Array.from(selectedLayers),
            statuses: Array.from(selectedOrderStatuses),
            hiddenElements: hasDbIds(hiddenElements)
              ? Object.entries(hiddenElements).map(([modelUrn, dbIds]) => ({
                  modelUrn,
                  dbIds,
                }))
              : null,
            isolatedElements: hasDbIds(isolatedElements)
              ? Object.entries(isolatedElements).map(([modelUrn, dbIds]) => ({
                  modelUrn,
                  dbIds,
                }))
              : null,
            hiddenModels: hiddenModelUrns,
            isDefault: false,
            isCommentView: true,
            cameraPosition: viewer ? getCameraPosition(viewer) : null,
          },
        },
      },
    };

    const result = await createCommentMutation({ input: createCommentInput });

    if (result.error) {
      console.error(result.error);
      toast({
        title: 'An error occurred when creating comment',
        status: 'error',
      });
    } else {
      toast({
        title: 'Comment successfully created',
        status: 'success',
      });
      //To avoid saved views menu to refresh the view
      if (!result.data?.createComment?.comment?.parentId)
        navigate(
          '?viewId=' +
            result.data?.createComment?.comment?.savedViews?.id +
            '&commentId=' +
            result.data?.createComment?.comment?.id,
          { replace: true }
        );

      trackEvent(EventName.CommentAdded, {
        'Project Name': projectName,
        'Project ID': projectId,
        Comment: result.data?.createComment?.comment?.content,
      });
    }
  }

  function addTopLevelComment(
    content: string,
    user: { username: string },
    selectedDbIds: DbIdsPerModel,
    anchorPoint: Point
  ) {
    const newComment: Comment = {
      createdAt: new Date(),
      content: content,
      user: { ...user },
      replies: [],
      projectId: projectId ? projectId : '',
      takeoffId: orderId,
      id: '',
      selectedDbIds: selectedDbIds,
      point: anchorPoint,
    };
    saveChanges(newComment);
  }

  // NewReply
  function reply(
    parentId: string,
    user: { username: string },
    content: string,
    replyingTo: string
  ) {
    const newComment: Comment = {
      createdAt: new Date(),
      content: content,
      replyingTo: replyingTo,
      replies: [],
      user: { ...user },
      projectId: projectId ? projectId : '',
      takeoffId: orderId,
      id: '',
      parentId: parentId,
    };

    saveChanges(newComment);
  }

  // function updateComment(id: number, parentId: number | null, content: string) {
  //   const newList = [...comments];
  //   if (!parentId) {
  //     const idx = newList.findIndex((comment) => comment.id === id);
  //     newList[idx].content = content;
  //     saveChanges(newList);
  //   } else {
  //     const parentIdx = findParentIndex(parentId);
  //     const idx = newList[parentIdx].replies.findIndex(
  //       (comment) => comment.id === id
  //     );
  //     newList[parentIdx].replies[idx].content = content;
  //     saveChanges(newList);
  //   }
  // }

  // Delete Comment
  function deleteComment(id: string) {
    console.log('deleteComment id: ' + id);
    deleteCommentMutation({ input: { id: id } });
  }

  const value = {
    saveChanges,
    reply,
    addTopLevelComment,
    deleteComment,
    showDeleteDialog,
    setDeleteDialogVisibility,
    currentCommentIdx,
    setCurrentCommentIdx,
  };

  return (
    <CommentsContext.Provider value={value}>
      {children}
    </CommentsContext.Provider>
  );
}

export default CommentsProvider;
